import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';
import { 
  Clock, 
  MapPin, 
  Phone, 
  Mail, 
  Calendar, 
  User, 
  Star,
  Bell,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingStatus = ({ bookingId, onBack }) => {
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReschedule, setShowReschedule] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({
    new_date: '',
    new_time: '',
    reason: ''
  });
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    if (bookingId) {
      loadBookingStatus();
      // Set up polling for real-time updates
      const interval = setInterval(loadBookingStatus, 30000); // 30 seconds
      return () => clearInterval(interval);
    }
  }, [bookingId]);

  const loadBookingStatus = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}/status`);
      setBookingData(response.data);
    } catch (error) {
      console.error('Error loading booking status:', error);
      toast({
        title: "Gabim",
        description: "Nuk mund të ngarkohen të dhënat e rezervimit.",
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  const handleReschedule = async () => {
    if (!rescheduleData.new_date || !rescheduleData.new_time) {
      toast({
        title: "Gabim",
        description: "Ju lutemi zgjidhni datën dhe orën e re.",
        variant: "destructive",
      });
      return;
    }

    try {
      await axios.post(`${API}/bookings/${bookingId}/reschedule`, rescheduleData);
      toast({
        title: "Sukses",
        description: "Rezervimi u programua përsëri me sukses!",
      });
      setShowReschedule(false);
      loadBookingStatus();
    } catch (error) {
      toast({
        title: "Gabim",
        description: "Nuk mund të programohet përsëri rezervimi.",
        variant: "destructive",
      });
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      toast({
        title: "Gabim",
        description: "Ju lutemi jepni një arsye për anulim.",
        variant: "destructive",
      });
      return;
    }

    try {
      await axios.post(`${API}/bookings/${bookingId}/cancel`, { reason: cancelReason });
      toast({
        title: "Sukses",
        description: "Rezervimi u anulua me sukses.",
      });
      setShowCancel(false);
      loadBookingStatus();
    } catch (error) {
      toast({
        title: "Gabim",
        description: "Nuk mund të anulohet rezervimi.",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'bg-yellow-100 text-yellow-800', text: 'Në Pritje', icon: <Clock className="h-4 w-4" /> },
      'confirmed': { color: 'bg-blue-100 text-blue-800', text: 'I Konfirmuar', icon: <CheckCircle className="h-4 w-4" /> },
      'in_progress': { color: 'bg-orange-100 text-orange-800', text: 'Në Progres', icon: <RefreshCw className="h-4 w-4" /> },
      'completed': { color: 'bg-green-100 text-green-800', text: 'I Përfunduar', icon: <CheckCircle className="h-4 w-4" /> },
      'cancelled': { color: 'bg-red-100 text-red-800', text: 'I Anuluar', icon: <XCircle className="h-4 w-4" /> }
    };
    
    const config = statusConfig[status] || statusConfig['pending'];
    return (
      <Badge className={config.color}>
        {config.icon}
        <span className="ml-1">{config.text}</span>
      </Badge>
    );
  };

  const getJobStatusBadge = (status) => {
    const statusConfig = {
      'available': { color: 'bg-blue-100 text-blue-800', text: 'I Disponueshëm' },
      'assigned': { color: 'bg-purple-100 text-purple-800', text: 'I Caktuar' },
      'in_progress': { color: 'bg-orange-100 text-orange-800', text: 'Në Progres' },
      'completed': { color: 'bg-green-100 text-green-800', text: 'I Përfunduar' },
      'cancelled': { color: 'bg-red-100 text-red-800', text: 'I Anuluar' }
    };
    
    const config = statusConfig[status] || statusConfig['available'];
    return <Badge className={config.color}>{config.text}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Duke ngarkuar statusin e rezervimit...</p>
        </div>
      </div>
    );
  }

  if (!bookingData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Rezervimi nuk u gjet.</p>
            <Button onClick={onBack}>Kthehu</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { booking, job, partner, notifications } = bookingData;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          
          {/* Header */}
          <div className="mb-8">
            <Button variant="ghost" onClick={onBack} className="mb-4">
              ← Kthehu
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Statusi i Rezervimit</h1>
            <p className="text-gray-600">Ndiqni përditësimet e rezervimit tuaj</p>
          </div>

          {/* Booking Overview */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl">Detajet e Rezervimit</CardTitle>
                {getStatusBadge(booking.status)}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-gray-500" />
                    <div>
                      <div className="font-semibold">{booking.customer_name}</div>
                      <div className="text-sm text-gray-600">{booking.customer_email}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-gray-500" />
                    <div className="font-semibold">{booking.customer_phone}</div>
                  </div>

                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-gray-500" />
                    <div className="font-semibold">{booking.address}</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-gray-500" />
                    <div>
                      <div className="font-semibold">{booking.preferred_date}</div>
                      <div className="text-sm text-gray-600">në {booking.preferred_time}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Star className="h-5 w-5 text-gray-500" />
                    <div>
                      <div className="font-semibold">{booking.service_type.replace('_', ' ').toUpperCase()}</div>
                      <div className="text-sm text-gray-600">€{booking.total_amount}</div>
                    </div>
                  </div>
                </div>
              </div>

              {booking.special_requirements && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-1">Kërkesat speciale:</h4>
                  <p className="text-gray-700">{booking.special_requirements}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Job Status */}
          {job && (
            <Card className="mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">Statusi i Punës</CardTitle>
                  {getJobStatusBadge(job.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-2">Detajet e Punës</h4>
                    <div className="space-y-2 text-sm">
                      <div>Kohëzgjatja e vlerësuar: {job.estimated_duration} orë</div>
                      <div>Çmimi i vlerësuar: €{job.estimated_price}</div>
                      <div>Data e planifikuar: {job.scheduled_date} në {job.scheduled_time}</div>
                    </div>
                  </div>

                  {partner && (
                    <div>
                      <h4 className="font-semibold mb-2">Partneri i Caktuar</h4>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                          <User className="h-6 w-6 text-emerald-600" />
                        </div>
                        <div>
                          <div className="font-semibold">{partner.name}</div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Star className="h-4 w-4 text-yellow-400 fill-current" />
                            <span>{partner.rating.toFixed(1)} ({partner.totalJobs} punë)</span>
                          </div>
                          <div className="text-sm text-gray-600">{partner.phone}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          {booking.status === 'pending' || booking.status === 'confirmed' ? (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-xl">Veprime</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4">
                  <Button 
                    onClick={() => setShowReschedule(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Programo Përsëri
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={() => setShowCancel(true)}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Anulo Rezervimin
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {/* Reschedule Modal */}
          {showReschedule && (
            <Card className="mb-6 border-blue-200">
              <CardHeader className="bg-blue-50">
                <CardTitle className="text-xl text-blue-800">Programo Përsëri Rezervimin</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="newDate">Data e re *</Label>
                      <Input
                        id="newDate"
                        type="date"
                        value={rescheduleData.new_date}
                        onChange={(e) => setRescheduleData(prev => ({ ...prev, new_date: e.target.value }))}
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="newTime">Ora e re *</Label>
                      <Input
                        id="newTime"
                        type="time"
                        value={rescheduleData.new_time}
                        onChange={(e) => setRescheduleData(prev => ({ ...prev, new_time: e.target.value }))}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="rescheduleReason">Arsyeja (opsionale)</Label>
                    <Textarea
                      id="rescheduleReason"
                      value={rescheduleData.reason}
                      onChange={(e) => setRescheduleData(prev => ({ ...prev, reason: e.target.value }))}
                      placeholder="Pse doni të programoni përsëri..."
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-4">
                    <Button onClick={handleReschedule} className="bg-blue-600 hover:bg-blue-700">
                      Konfirmo Reprogramimin
                    </Button>
                    <Button variant="outline" onClick={() => setShowReschedule(false)}>
                      Anulo
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cancel Modal */}
          {showCancel && (
            <Card className="mb-6 border-red-200">
              <CardHeader className="bg-red-50">
                <CardTitle className="text-xl text-red-800">Anulo Rezervimin</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="cancelReason">Arsyeja e anulimit *</Label>
                    <Textarea
                      id="cancelReason"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="Pse doni të anuloni rezervimin..."
                      rows={3}
                      required
                    />
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800 text-sm">
                      <strong>Vërejtje:</strong> Anulimi i rezervimit do të njoftojë partnerin e caktuar (nëse ka) dhe nuk mund të zhbëhet.
                    </p>
                  </div>

                  <div className="flex gap-4">
                    <Button 
                      onClick={handleCancel} 
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Konfirmo Anulimin
                    </Button>
                    <Button variant="outline" onClick={() => setShowCancel(false)}>
                      Mbaj Rezervimin
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notifications */}
          {notifications && notifications.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Njoftimet
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {notifications.map((notification) => (
                    <div key={notification.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <Bell className="h-5 w-5 text-blue-500 mt-1" />
                      <div className="flex-1">
                        <p className="text-gray-900">{notification.message}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          {format(new Date(notification.sent_at), 'dd/MM/yyyy HH:mm')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingStatus;